import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import Quill from "quill";
import ImageUploadModal from "./ImageUploadModal";
import "quill/dist/quill.snow.css";
import "./QuillWrapper.scss";

// Configure Quill Size whitelist with custom pixel values
const Size = Quill.import("formats/size");
Size.whitelist = ["12px", "14px", "16px", "18px", "24px", "36px"];
Quill.register(Size, true);

// Custom Image Blot with width/height support
const BlockEmbed = Quill.import("blots/block/embed");

class ImageBlot extends BlockEmbed {
  static blotName = "image";
  static tagName = "img";

  static create(value) {
    const node = super.create();

    node.setAttribute("src", value.url || value);

    node.style.width = value.width;
    node.style.height = value.height;

    return node;
  }

  static value(node) {
    return {
      url: node.getAttribute("src"),
      width: node.style.width,
      height: node.style.height
    };
  }
}

Quill.register(ImageBlot, true);

// Pixel width of the in-app notification display area. Images wider than this
// fall back to height:auto so they scale down instead of overflowing the notice.
const NOTIFICATION_AREA_WIDTH = 1287;

// Quill formats - static, never changes
const formats = [
  "size",
  "bold",
  "italic",
  "link",
  "color",
  "background",
  "list",
  "align",
  "image"
];

// Toolbar configuration structure - static, never changes
const toolbarConfig = [
  ["undo", "redo"],
  [{ size: ["12px", "14px", "16px", "18px", "24px", "36px"] }],
  ["bold", "italic", "link"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["image"],
  ["clean"]
];

const QuillWrapper = ({ value, onChange, onBlur }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const defaultValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Keep the latest callbacks without re-initializing the editor
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
  });

  // Instantiate Quill once on mount. The editor remounts fresh on each Add/Edit
  // with the value already in Redux, so the initial paste is all that's needed.
  useEffect(() => {
    const editorContainer = containerRef.current.appendChild(document.createElement("div"));

    const quill = new Quill(editorContainer, {
      theme: "snow",
      formats,
      modules: {
        toolbar: {
          container: toolbarConfig,
          handlers: {
            undo: () => quillRef.current?.history.undo(),
            redo: () => quillRef.current?.history.redo(),
            image: () => setIsImageModalOpen(true)
          }
        }
      }
    });

    quillRef.current = quill;

    if (defaultValueRef.current) {
      quill.clipboard.dangerouslyPasteHTML(defaultValueRef.current);
    }

    // Push HTML out on every edit; a null selection range signals blur
    quill.on(Quill.events.TEXT_CHANGE, () => onChangeRef.current?.(quill.root.innerHTML));
    quill.on(Quill.events.SELECTION_CHANGE, (range) => {
      if (range === null) onBlurRef.current?.();
    });

    return () => {
      quillRef.current = null;
      containerRef.current.innerHTML = "";
    };
  }, []);

  // Insert the image (with dimensions) picked in the modal at the cursor
  const handleImageSave = (imageData) => {
    const quill = quillRef.current;
    if (!quill) return;

    const range = quill.getSelection(true);

    quill.insertEmbed(range.index, "image", imageData);
    quill.setSelection(range.index + 1);
  };

  return (
    <>
      <div className="quill-wrapper">
        <div className="quill-wrapper__editor" ref={containerRef} />
      </div>
      {isImageModalOpen && (
        <ImageUploadModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          onSave={handleImageSave}
          maxWidth={NOTIFICATION_AREA_WIDTH}
        />
      )}
    </>
  );
};

export default QuillWrapper;
